<template>
  <div class="language-dropdown">
    <VaDropdown placement="bottom-end" :offset="[0, 8]">
      <template #anchor>
        <button class="glass-toggle" type="button" aria-label="Change language">
          <VaIcon name="language" size="medium" />
          <span class="code">{{ locale.toUpperCase() }}</span>
          <VaIcon class="chevron" name="expand_more" size="small" />
        </button>
      </template>

      <VaDropdownContent class="dropdown-content">
        <button
          v-for="(name, code) in languages"
          :key="code"
          :class="['dropdown-item', { active: locale === code }]"
          @click="locale = code"
        >
          <VaIcon name="language" size="medium" />
          <span class="name">{{ name }} ({{ getFlag(code) }})</span>
          <VaIcon v-if="locale === code" name="check" size="small" />
        </button>
      </VaDropdownContent>
    </VaDropdown>
  </div>
</template>

<script lang="ts" setup>
import { useI18n } from 'vue-i18n'
import { onMounted, watch } from 'vue'

type FlagMap = Record<string, string>

// Helper: sessionStorage for agents (impersonation or direct login), localStorage for admin
const getStorage = (): Storage => {
  // Check if it's impersonation session
  if (sessionStorage.getItem('impersonation_session')) {
    return sessionStorage
  }
  
  // Check if it's agent direct login
  if (sessionStorage.getItem('admin_token') && sessionStorage.getItem('role') === 'agent') {
    return sessionStorage
  }
  
  // Default: admin using localStorage
  return localStorage
}

const { locale } = useI18n()

// Initialiser la langue au montage
onMounted(() => {
  const storage = getStorage()
  const storedLocale = storage.getItem('app_locale')
  if (storedLocale && storedLocale in languages) {
    locale.value = storedLocale
  }
})

// Sauvegarder la langue quand elle change
watch(locale, (newLocale) => {
  const storage = getStorage()
  storage.setItem('app_locale', newLocale)
  
  // Émettre un événement pour notifier le changement de langue
  window.dispatchEvent(new CustomEvent('languageChanged', { detail: { locale: newLocale } }))
})

const languages = {
  gb: 'English',
  cn: '中文',
 
}

const flagMap: FlagMap = {
  gb: '��',
  cn: '🇨🇳',

}

const getFlag = (code: string): string => {
  const codes: Record<string, string> = { gb: 'GB',cn: 'CN'}
  return codes[code] || 'EN'
}
</script>

<style scoped>
.language-dropdown {
  min-width: 0;
}

.glass-toggle {
  padding: 0.5rem 0.75rem;
  border: none;
  border-radius: 0.625rem;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  cursor: pointer;
  font-size: 1rem;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  white-space: nowrap;
}

.glass-toggle:hover {
  background: rgba(255, 255, 255, 0.15);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.glass-toggle:active {
  background: rgba(255, 255, 255, 0.3);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
}

.flag {
  line-height: 1;
}

.code {
  font-size: 0.8125rem;
  font-weight: 600;
  letter-spacing: 0.05em;
}

.chevron {
  opacity: 0.8;
}

.dropdown-content {
  min-width: 180px;
}

.dropdown-item {
  width: 100%;
  padding: 0.75rem 0.875rem;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.875rem;
  transition: all 0.2s ease;
  text-align: left;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  &.active {
    background: rgba(255, 255, 255, 0.15);
    font-weight: 500;
  }
}

.name {
  flex: 1;
}

@media (max-width: 640px) {
  .glass-toggle {
    padding: 0.4375rem 0.5625rem;
    border-radius: 0.5625rem;
    gap: 0.25rem;
  }

  .code {
    font-size: 0.75rem;
  }
}

@media (max-width: 480px) {
  .glass-toggle {
    padding: 0.4375rem;
  }

  .code,
  .chevron {
    display: none;
  }
}
</style>
